import { and, eq } from 'drizzle-orm';
import type { SQLiteTable } from 'drizzle-orm/sqlite-core';
import {
  outbox,
  lastPulledAt,
  profiles,
  projects,
  supplies,
  projectSupplies,
  timeEntries,
  counters,
  counterEvents,
  projectPhotos,
} from '../db/schema';
import { getPending, incrementAttempt, markProcessed, markQuarantined, type AppDatabase } from '../db/outbox';
import { supabase } from '../lib/supabase';

export type { AppDatabase };

// --- Subconjunto do client supabase-js usado por pushAll/pullAll --------
//
// Tipado explicitamente (em vez de `SupabaseClient` do supabase-js) para
// permitir injetar, em teste, um fake in-memory que simula um "servidor"
// Supabase compartilhado entre dois dispositivos sem precisar mockar o
// pacote inteiro (auth, storage, realtime, etc. não são usados aqui).
export interface SupabaseQueryResult<T = any> {
  data: T | null;
  // `code` é o código de erro do Postgrest/Postgres (ex.: '23505' unique
  // violation) quando disponível — usado só para compor uma mensagem de
  // quarentena legível (ver formatPushError), não para decidir se o erro é
  // recuperável: essa decisão já vem de graça de resolve-vs-reject (ver
  // comentário em pushAll).
  error: { message: string; code?: string } | null;
}

export interface SupabaseTableClient {
  insert(data: any): PromiseLike<SupabaseQueryResult>;
  // Upsert por `id` (chave primária em toda tabela deste projeto) — usado
  // por pushAll para INSERT e UPDATE (ver comentário lá). O client real do
  // supabase-js já expõe `.upsert()` nativamente (confirmado na versão
  // instalada, @supabase/postgrest-js), então não precisa de nenhuma
  // mudança em src/lib/supabase.ts — só declarar aqui no subconjunto
  // tipado usado por pushAll/pullAll.
  upsert(data: any): PromiseLike<SupabaseQueryResult>;
  update(data: any): { eq(column: string, value: any): PromiseLike<SupabaseQueryResult> };
  delete(): { eq(column: string, value: any): PromiseLike<SupabaseQueryResult> };
  select(columns: string): {
    gt(
      column: string,
      value: string
    ): {
      order(column: string, opts: { ascending: boolean }): PromiseLike<SupabaseQueryResult<any[]>>;
    };
    // Usado só pelo guard de LWW no push (ver pushAll) para ler o
    // `updated_at` remoto atual de UMA linha antes de decidir se upserta por
    // cima dela.
    eq(column: string, value: any): PromiseLike<SupabaseQueryResult<any[]>>;
  };
}

export interface SupabaseLikeClient {
  from(table: string): SupabaseTableClient;
}

export interface SyncService {
  pushAll(db: AppDatabase): Promise<void>;
  pullAll(db: AppDatabase): Promise<void>;
  syncAll(db: AppDatabase): Promise<void>;
  getStatus(db: AppDatabase): Promise<SyncStatus[]>;
}

export interface SyncStatus {
  tableName: string;
  lastSyncedAt: number;
  pendingCount: number;
  // Falha #6 (docs/crochet/2026-07-15-RelatorioAceite.md): itens em
  // quarentena (push com erro não recuperável, ver markQuarantined em
  // db/outbox.ts) são escritas presas — não contam como "pendente" (não
  // voltam a ser tentadas sozinhas), mas também não são "sincronizadas".
  // Contado à parte para que o indicador de nuvem em ProfileScreen não
  // mostre "sincronizado" com uma escrita perdida.
  quarantinedCount: number;
  error?: string;
}

// Tabelas cujo padrão de conflito é "append-only" (spec S05 — ver
// docs/crochet/specs/2026-07-06-S05-sync-offline.md): nunca update/delete,
// só insert. Duas linhas de dispositivos diferentes nunca se sobrescrevem —
// os eventos se somam. Essas tabelas não têm coluna `updated_at` (não faz
// sentido para uma linha imutável), então o cursor de pull incremental usa
// outra coluna monotônica própria da tabela.
const APPEND_ONLY_SYNC_COLUMN: Record<string, string> = {
  counter_events: 'at',
};

const DEFAULT_SYNC_COLUMN = 'updated_at';

export class SyncServiceImpl implements SyncService {
  private readonly supabaseClient: SupabaseLikeClient;

  constructor(supabaseClient?: SupabaseLikeClient) {
    // Fallback pro client real do supabase-js quando nenhum for injetado
    // (uso normal do app) — o mesmo client compartilhado usado pelo auth
    // (src/lib/supabase.ts), nunca uma instância própria: duas instâncias
    // de `createClient` para o mesmo projeto rodam dois GoTrueClient/
    // listeners de auth concorrentes escrevendo na mesma storage. O cast é
    // necessário porque `SupabaseLikeClient` é deliberadamente um
    // subconjunto estreito da API real (só o que pushAll/pullAll usam) para
    // ser fácil de fakear em teste — o client real do supabase-js tem uma
    // superfície muito maior (auth, storage, realtime, rpc...) e seus
    // builders (`PostgrestFilterBuilder`) não são um `Promise` estrutural
    // exato, então o TS não infere a compatib(que existe na prática) sozinho.
    this.supabaseClient = supabaseClient ?? (supabase as unknown as SupabaseLikeClient);
  }

  async pushAll(db: AppDatabase): Promise<void> {
    // Drena a fila inteira nesta chamada, não só o primeiro lote de
    // getPending (que tem LIMIT — ver src/db/outbox.ts): uma semana offline
    // acumula facilmente mais itens que um lote só, e sem este loop uma
    // única chamada de sync nunca esvaziava a fila (bug da Etapa 6). Cada
    // volta busca um lote novo porque markProcessed/markQuarantined desta
    // volta já tiraram os itens resolvidos de getPending (que filtra
    // status='pending') — converge sem reprocessar o que já foi resolvido.
    for (;;) {
      const pending = await getPending(db);
      if (!pending.length) return;

      for (const item of pending) {
        try {
          // Convenção: o payload guardado na outbox já está no formato "de
          // fio" (snake_case, igual às colunas do Postgres) — é enviado
          // como está para `.upsert()`/`.delete()`. Ver mapLocalRecordToWire.
          const data = JSON.parse(item.payload as string);
          const table = this.getTableByName(item.tableName);
          if (!table) {
            // Tabela desconhecida na outbox (dado corrompido/schema
            // divergente) nunca vai se resolver sozinha — quarentena em vez
            // de `continue` puro: sem isso o item ficaria 'pending' para
            // sempre e o loop de drenagem acima nunca convergiria.
            await markQuarantined(db, item.id, `Tabela desconhecida: ${item.tableName}`);
            continue;
          }

          const supabaseTable = this.supabaseClient.from(item.tableName);

          // Guard de LWW no PUSH (tabelas com `updated_at` — não as
          // append-only, ver APPEND_ONLY_SYNC_COLUMN): sem isto, `upsert`
          // sobrescreve a linha remota INCONDICIONALMENTE — "quem empurra
          // por último vence", não "quem editou por último vence". Isso
          // diverge do `pull`, que já respeita `updated_at` via
          // `mergeRecord`, e pode produzir split-brain permanente: se o
          // dispositivo com a edição MAIS NOVA sincronizar primeiro e o
          // MAIS ANTIGO depois, o antigo sobrescreveria o novo no servidor,
          // e cada aparelho convergiria para um nome diferente (o pull de
          // cada um vê o servidor como "não mais novo que o local" e nunca
          // se corrige). Ler o `updated_at` remoto antes de upsertar é o
          // mesmo espírito de simplicidade da spec (sem CRDT): só um SELECT
          // extra por item, e SÓ quando a tabela participa de LWW por
          // updated_at de verdade.
          const syncColumn = APPEND_ONLY_SYNC_COLUMN[item.tableName];
          if (item.operation !== 'DELETE' && !syncColumn && typeof data.updated_at === 'string') {
            const { data: remoteRows } = await supabaseTable.select('updated_at').eq('id', item.recordId);
            const remoteUpdatedAt = remoteRows?.[0]?.updated_at;
            if (
              typeof remoteUpdatedAt === 'string' &&
              new Date(remoteUpdatedAt).getTime() > new Date(data.updated_at).getTime()
            ) {
              // O remoto já é mais novo que a escrita local sendo
              // empurrada — não upserta por cima dele. Não é erro nem
              // quarentena: é uma escrita local desatualizada, resolvida
              // normalmente (o próximo pull traz a versão remota mais nova
              // para este dispositivo).
              await markProcessed(db, item.id);
              continue;
            }
          }

          // INSERT e UPDATE viram upsert: um retry depois de um push
          // anterior já ter sido aplicado ao servidor (ex.: app matado
          // entre o servidor confirmar e o outbox local gravar
          // markProcessed) bateria em 23505 unique_violation com um
          // `.insert()` puro — upsert por id (chave primária em toda
          // tabela) é idempotente por natureza. Soft delete também passa
          // por aqui como UPDATE, nunca DELETE físico (ver
          // softDeleteRecord em src/db/writes.ts). DELETE físico (fora do
          // purge, fora de escopo desta etapa) continua usando `.delete()`
          // — não faz sentido upsertar uma exclusão.
          const { error } =
            item.operation === 'DELETE'
              ? await supabaseTable.delete().eq('id', item.recordId)
              : await supabaseTable.upsert(data);

          if (error) {
            // supabase-js só REJEITA a promise (cai no catch abaixo) numa
            // falha de transporte/rede de verdade — um erro do Postgrest
            // (4xx: constraint, RLS etc.) volta como `{ error }` na
            // resposta RESOLVIDA. Ou seja, chegar aqui já significa "não é
            // rede": não precisa reclassificar por código, retentar não
            // muda o resultado. Quarentena em vez de `markProcessed` —
            // perder a escrita em silêncio é exatamente o bug que esta
            // etapa corrige (spec S05 §Estados na UI).
            await markQuarantined(db, item.id, formatPushError(error));
            continue;
          }

          await markProcessed(db, item.id);
        } catch (error) {
          // Falha de rede/transporte: vale tentar de novo mais tarde, mas
          // insistir nos próximos itens deste mesmo lote agora é só bater
          // na mesma parede de novo — para o drenar inteiro aqui em vez de
          // continuar o loop.
          console.error(`Error pushing item ${item.id} to Supabase:`, error);
          await incrementAttempt(db, item.id);
          return;
        }
      }
    }
  }

  async pullAll(db: AppDatabase): Promise<void> {
    const tables = this.getTableNames();

    for (const tableName of tables) {
      const syncColumn = APPEND_ONLY_SYNC_COLUMN[tableName] ?? DEFAULT_SYNC_COLUMN;

      const lastPulled = await db
        .select({ pulledAt: lastPulledAt.pulledAt })
        .from(lastPulledAt)
        .where(eq(lastPulledAt.tableName, tableName))
        .get();

      const { data, error } = await this.supabaseClient
        .from(tableName)
        .select('*')
        .gt(syncColumn, (lastPulled?.pulledAt ?? new Date(0)).toISOString())
        .order(syncColumn, { ascending: true });

      if (error) {
        console.error(`Error pulling from table ${tableName}:`, error);
        continue;
      }

      const table = this.getTableByName(tableName);
      if (!table) continue;

      const rows = (data as any[]) ?? [];
      for (const record of rows) {
        await this.mergeRecord(db, table, record);
      }

      // Cursor = maior valor de `syncColumn` dentre as linhas RECEBIDAS
      // nesta chamada (rows já vem ordenado ascendente por syncColumn — ver
      // `.order()` acima —, então a última linha tem o maior valor), NUNCA
      // o relógio do dispositivo leitor. `new Date()` aqui compararia o
      // relógio de quem lê contra o relógio de quem escreveu (via
      // updated_at/at): se o relógio de quem escreve está atrasado, uma
      // escrita feita DEPOIS do último pull pode carregar um timestamp
      // MENOR que um cursor já salvo — e como o cursor nunca anda pra trás,
      // essa escrita fica invisível para sempre no próximo pull (bug real,
      // ver RelatorioAceite S05 item 6). Sem linha nova recebida não há
      // valor de servidor para avançar o cursor — mantém o que já estava
      // salvo em vez de usar o relógio local como fallback.
      if (rows.length > 0) {
        const newCursor = new Date(rows[rows.length - 1][syncColumn]);
        await db
          .insert(lastPulledAt)
          .values({ tableName, pulledAt: newCursor })
          .onConflictDoUpdate({
            target: lastPulledAt.tableName,
            set: { pulledAt: newCursor },
          });
      }
    }
  }

  async syncAll(db: AppDatabase): Promise<void> {
    await this.pushAll(db);
    await this.pullAll(db);
  }

  async getStatus(db: AppDatabase): Promise<SyncStatus[]> {
    const result: SyncStatus[] = [];
    const tables = this.getTableNames();

    for (const tableName of tables) {
      const lastPulled = await db
        .select({ pulledAt: lastPulledAt.pulledAt })
        .from(lastPulledAt)
        .where(eq(lastPulledAt.tableName, tableName))
        .get();

      const pendingRows = await db
        .select()
        .from(outbox)
        .where(and(eq(outbox.status, 'pending'), eq(outbox.tableName, tableName)));

      // Mesma query de pendingRows acima, só trocando o filtro de status —
      // reaproveita a ideia de getQuarantined (db/outbox.ts) mas por tabela,
      // igual a pendingCount, em vez de uma lista achatada (Falha #6).
      const quarantinedRows = await db
        .select()
        .from(outbox)
        .where(and(eq(outbox.status, 'quarantined'), eq(outbox.tableName, tableName)));

      result.push({
        tableName,
        lastSyncedAt: lastPulled?.pulledAt?.getTime() || 0,
        pendingCount: pendingRows.length,
        quarantinedCount: quarantinedRows.length,
      });
    }

    return result;
  }

  private getTableNames(): string[] {
    return [
      'profiles',
      'projects',
      'supplies',
      'project_supplies',
      'time_entries',
      'counters',
      'counter_events',
      'project_photos',
    ];
  }

  private getTableByName(name: string): SQLiteTable | null {
    const tableMap: Record<string, SQLiteTable> = {
      profiles,
      projects,
      supplies,
      project_supplies: projectSupplies,
      time_entries: timeEntries,
      counters,
      counter_events: counterEvents,
      project_photos: projectPhotos,
    };
    return tableMap[name] || null;
  }

  private async mergeRecord(db: AppDatabase, table: SQLiteTable, record: Record<string, any>): Promise<void> {
    const mapped = mapWireRecordToLocal(table, record);
    const id = mapped.id;
    const existing = await db.select().from(table).where(eq((table as any).id, id)).get();

    if (!existing) {
      await db.insert(table).values(mapped);
      return;
    }

    if (!('updatedAt' in mapped)) {
      // Tabela append-only (ex.: counter_events): o registro já foi
      // mesclado antes (mesmo id) — nada a atualizar, nunca sobrescrever.
      return;
    }

    // Última escrita vence, comparando por updated_at (spec S05 §Conflitos).
    // Sem essa comparação, um pull poderia trazer de volta uma versão
    // desatualizada do servidor por cima de uma edição local mais recente
    // ainda não enviada (ex.: A edita o nome do projeto offline, o pull de
    // uma versão antiga do mesmo projeto sobrescreveria a edição de A).
    const remoteUpdatedAt = mapped.updatedAt instanceof Date ? mapped.updatedAt.getTime() : 0;
    const localUpdatedAt =
      (existing as any).updatedAt instanceof Date ? (existing as any).updatedAt.getTime() : 0;

    if (remoteUpdatedAt >= localUpdatedAt) {
      await db.update(table).set(mapped).where(eq((table as any).id, id));
    }
  }
}

/** Mensagem de quarentena legível a partir do erro devolvido pelo
 * Supabase/Postgrest — inclui o código (ex.: '23505') quando disponível,
 * útil para diagnosticar sem precisar reproduzir o erro. */
function formatPushError(error: { message: string; code?: string }): string {
  return error.code ? `${error.message} (${error.code})` : error.message;
}

// --- Mapeamento entre o shape "de fio" (snake_case, Postgres/JSON) e o ---
// --- shape local do Drizzle (camelCase, chaves = nomes de propriedade) ---
//
// `db.insert(table).values(record)` espera um objeto cujas chaves são os
// nomes de propriedade definidos no schema Drizzle (ex.: `counterId`), não
// os nomes de coluna SQL (`counter_id`). Um registro vindo do Supabase (ou
// de um payload de outbox) usa nomes de coluna snake_case — sem essa
// conversão, os valores são silenciosamente ignorados (a linha some) ou
// disparam erro de NOT NULL. A conversão usa os metadados reais das
// colunas do schema (nome SQL + `mode`) em vez de uma transformação
// snake<->camel "adivinhada" por regex, porque nem toda coluna segue a
// conversão óbvia (ex.: `lastAttemptAt` → `last_attempted_at`).

function isColumn(value: unknown): value is { name: string; mode?: string } {
  return !!value && typeof value === 'object' && typeof (value as any).name === 'string';
}

export function mapWireRecordToLocal(table: SQLiteTable, record: Record<string, any>): Record<string, any> {
  const local: Record<string, any> = {};
  for (const [jsKey, column] of Object.entries(table as unknown as Record<string, unknown>)) {
    if (!isColumn(column)) continue;
    if (!(column.name in record)) continue;

    let value = record[column.name];
    if (value !== null && value !== undefined) {
      if (column.mode === 'timestamp' || column.mode === 'timestamp_ms') {
        value = value instanceof Date ? value : new Date(value);
      } else if (column.mode === 'boolean') {
        value = typeof value === 'boolean' ? value : Boolean(value);
      }
    }
    local[jsKey] = value;
  }
  return local;
}

export function mapLocalRecordToWire(table: SQLiteTable, record: Record<string, any>): Record<string, any> {
  const wire: Record<string, any> = {};
  for (const [jsKey, column] of Object.entries(table as unknown as Record<string, unknown>)) {
    if (!isColumn(column)) continue;
    if (!(jsKey in record)) continue;

    let value = record[jsKey];
    if (value instanceof Date) {
      value = value.toISOString();
    }
    wire[column.name] = value;
  }
  return wire;
}
